// highlight.ts - the dbt charts editor palette. `language.ts` emits highlight
// tags and knows no colors; this file is the only place in the package that
// picks one. Both Cloud and the Playground mount it in place of CodeMirror's
// stock `defaultHighlightStyle`, an upstream demo palette unrelated to the
// dbt charts chrome.
//
// The rules it encodes live in
// dbt-charts/src/dbt_charts/data/highlighting/AGENTS.md. The two that shape it:
// structure outranks content (a key is louder than the value it names), and
// saturated color is spent only on semantic distinctions - a quoted and an
// unquoted string are the same value, so they get the same color.
//
// The emitter set is closed and enumerable - `@lezer/yaml`'s `styleTags`, the
// legacy SQL mode's return values, `tokenJinja`, and the scalar tokenizer - so
// every rule below has something that produces it. Don't add speculative ones.
//
// This is the *light* palette. Cloud also offers a dark editor theme (oneDark),
// which carries its own syntax colors; Cloud therefore mounts this with
// `syntaxHighlighting(..., { fallback: true })` so oneDark wins when selected.

import { HighlightStyle } from "@codemirror/language";
import { tags } from "@lezer/highlight";

// Anchored on the light dbt charts chrome (--dbt-system-accent #203c65,
// --dbt-system-muted #626366, --dbt-system-text #222222).
const KEY = "#203c65"; // YAML keys - the structure, so the loudest thing here
const STRING = "#3d6b4f"; // every string, quoted or plain
const CONSTANT = "#9a5b13"; // numbers, booleans, nulls - the non-string scalars
const KEYWORD = "#6f42c1"; // SQL keywords, Jinja statement blocks
const TYPE = "#0550ae"; // SQL functions and builtins, YAML tags
const TEMPLATE = "#0f766e"; // Jinja expressions, YAML anchors and aliases
const MUTED = "#626366"; // block-scalar headers, operators
// Separators and brackets - present, not read. Deliberately below the WCAG AA
// text ratio (2.6:1 on white): `:` and `-` are inferable from layout, and the
// hierarchy depends on them receding. Everything a reader actually reads clears
// AA, comments included.
const FAINT = "#9aa0a8";
const COMMENT = "#6b7280"; // 4.8:1 on white - prose, so it has to clear AA

export const dbtChartsHighlightStyle = HighlightStyle.define([
  { tag: tags.definition(tags.propertyName), color: KEY, fontWeight: "500" },
  { tag: [tags.separator, tags.punctuation, tags.squareBracket, tags.brace], color: FAINT },

  { tag: tags.string, color: STRING },
  { tag: [tags.number, tags.bool, tags.null, tags.atom], color: CONSTANT },
  { tag: tags.special(tags.string), color: MUTED, fontWeight: "500" }, // the `|` / `>` marker
  { tag: [tags.labelName, tags.meta], color: TEMPLATE },
  { tag: [tags.typeName, tags.macroName], color: TYPE },
  { tag: tags.keyword, color: KEYWORD },
  { tag: tags.operator, color: MUTED },
  { tag: tags.comment, color: COMMENT, fontStyle: "italic" },
]);
